import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Autocomplete,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  MenuItem,
  Typography,
  Divider,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as BankIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { Employee } from '../../types/employees.types';
import employeesService from '../../services/employees.service';
import { branchesService } from '../../services/branches.service';
import banksService, { Bank } from '../../services/banks.service';
import { Branch } from '../../types/branches.types';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';

interface EmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  employee?: Employee | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const translations = {
  en: {
    titleNew: 'New Employee',
    titleEdit: 'Edit Employee',
    employeeCode: 'Employee Code',
    fullName: 'Full Name',
    fullNameAr: 'Full Name (Arabic)',
    branch: 'Branch',
    jobTitle: 'Job Title',
    phone: 'Phone',
    email: 'Email',
    hireDate: 'Hire Date',
    cancel: 'Cancel',
    save: 'Save',
    basicInfoTab: 'Basic Information',
    salarySettingsTab: 'Salary Settings',
    employeeType: 'Employee Type',
    management: 'Management',
    branchEmployee: 'Branch',
    dailyWorkHours: 'Daily Work Hours',
    basicSalary: 'Basic Salary',
    housingAllowance: 'Housing Allowance',
    transportAllowance: 'Transport Allowance',
    otherAllowances: 'Other Allowances',
    overtimeCalculation: 'Overtime Calculation',
    calculationBase: 'Calculation Base',
    fromBasicSalary: 'From Basic Salary',
    fromTotalSalary: 'From Total Salary',
    fixedHourlyRate: 'Fixed Hourly Rate',
    multiplier: 'Multiplier',
    hourlyRate: 'Hourly Rate (KWD)',
    publicHolidayCalculation: 'Public Holiday Calculation',
    bankInfo: 'Bank Information',
    bankName: 'Bank Name',
    bankFileType: 'Bank File Type',
    regularSalary: 'Regular Salary',
    workerSalary: 'Worker Salary (BD)',
    accountNumber: 'Account Number',
    iban: 'IBAN',
    bankCardName: 'Name on Bank Card',
    companyFileNumber: 'Company File Number (شؤون)',
    paymentMethod: 'Payment Methods',
    cash: 'Cash',
    bankTransfer: 'Bank Transfer',
    saveSuccess: 'Employee saved successfully',
    updateSuccess: 'Employee updated successfully',
    saveError: 'Failed to save employee',
    kwd: 'KWD'
  },
  ar: {
    titleNew: 'موظف جديد',
    titleEdit: 'تعديل موظف',
    employeeCode: 'رمز الموظف',
    fullName: 'الاسم الكامل',
    fullNameAr: 'الاسم الكامل (عربي)',
    branch: 'الفرع',
    jobTitle: 'المسمى الوظيفي',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    hireDate: 'تاريخ التعيين',
    cancel: 'إلغاء',
    save: 'حفظ',
    basicInfoTab: 'المعلومات الأساسية',
    salarySettingsTab: 'إعدادات الراتب',
    employeeType: 'نوع الموظف',
    management: 'إدارة',
    branchEmployee: 'فرع',
    dailyWorkHours: 'ساعات الدوام اليومي',
    basicSalary: 'الراتب الأساسي',
    housingAllowance: 'بدل السكن',
    transportAllowance: 'بدل المواصلات',
    otherAllowances: 'بدلات أخرى',
    overtimeCalculation: 'حساب الوقت الإضافي',
    calculationBase: 'أساس الحساب',
    fromBasicSalary: 'من الراتب الأساسي',
    fromTotalSalary: 'من إجمالي الراتب',
    fixedHourlyRate: 'سعر ثابت للساعة',
    multiplier: 'المضاعف',
    hourlyRate: 'سعر الساعة (د.ك)',
    publicHolidayCalculation: 'حساب العطلات الرسمية',
    bankInfo: 'معلومات البنك',
    bankName: 'اسم البنك',
    bankFileType: 'نوع الملف البنكي',
    regularSalary: 'رواتب عادية',
    workerSalary: 'رواتب عمال (بي دينار)',
    accountNumber: 'رقم الحساب',
    iban: 'الآيبان',
    bankCardName: 'الاسم على كارت البنك',
    companyFileNumber: 'رقم ملف الشركة (الشؤون)',
    paymentMethod: 'طرق الدفع',
    cash: 'نقدي',
    bankTransfer: 'تحويل بنكي',
    saveSuccess: 'تم حفظ الموظف بنجاح',
    updateSuccess: 'تم تحديث الموظف بنجاح',
    saveError: 'فشل حفظ الموظف',
    kwd: 'د.ك'
  },
};

const EmployeeDialog: React.FC<EmployeeDialogProps> = ({ open, onClose, onSave, employee }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<Employee>({
    employeeId: 0,
    employeeCode: '',
    fullName: '',
    fullNameAr: '',
    branchId: undefined,
    departmentId: undefined,
    jobTitle: '',
    phone: '',
    email: '',
    hireDate: new Date().toISOString().split('T')[0],
    isActive: true,
    employeeType: 'Branch',
    dailyWorkHours: 8.000,
    basicSalary: 0.000,
    housingAllowance: 0.000,
    transportAllowance: 0.000,
    otherAllowances: 0.000,
    overtimeCalculationBase: 'FromBasicSalary',
    overtimeMultiplier: 1.500,
    overtimeHourlyRate: 0.000,
    publicHolidayCalculationBase: 'FromBasicSalary',
    publicHolidayMultiplier: 2.000,
    publicHolidayHourlyRate: 0.000,
    offDayCalculationBase: 'FromBasicSalary',
    offDayMultiplier: 1.500,
    offDayHourlyRate: 0.000,
    bankAccountNumber: '',
    bankName: 'Burgan Bank',
    iban: '',
    bankCardName: '',
    companyFileNumber: '000000000000',
    bankFileType: 'Regular',
    basicSalaryPaymentMethod: 'BankTransfer',
    allowancesPaymentMethod: 'BankTransfer',
    overtimePaymentMethod: 'Cash',
    holidayPaymentMethod: 'Cash',
    offDayPaymentMethod: 'Cash'
  });

  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);

  useEffect(() => {
    if (open) {
      if (employee) {
        setFormData({
          ...employee,
          hireDate: employee.hireDate
            ? employee.hireDate.split('T')[0]
            : new Date().toISOString().split('T')[0],
        });
      } else {
        generateEmployeeCode();
      }
      loadBranches();
      loadBanks();
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee]);

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
      
      if (employee && employee.branchId) {
        const branch = data.find((b: Branch) => b.branchId === employee.branchId);
        setSelectedBranch(branch || null);
      }
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadBanks = async () => {
    try {
      const data = await banksService.getAll();
      
      // Set selectedBank BEFORE filtering (to include inactive banks if employee has one)
      if (employee) {
        let bank = null;
        // Try bankId first (preferred)
        if (employee.bankId) {
          bank = data.find((b: Bank) => b.bankId === employee.bankId);
        }
        // Fallback to bankName if bankId not found
        if (!bank && employee.bankName) {
          bank = data.find((b: Bank) => 
            b.bankName === employee.bankName || b.bankNameAr === employee.bankName
          );
        }
        setSelectedBank(bank || null);
      }
      
      // Now filter active banks for dropdown
      setBanks(data.filter((b: Bank) => b.isActive));
    } catch (error) {
      console.error('Failed to load banks:', error);
    }
  };

  const generateEmployeeCode = async () => {
    try {
      const response = await employeesService.generateEmployeeCode();
      setFormData((prev) => ({ ...prev, employeeCode: response.data }));
    } catch (error) {
      console.error('Failed to generate employee code:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const newBranchId = selectedBranch?.branchId;
      const oldBranchId = employee?.branchId;

      // ✅ تحذير المستخدم قبل تحويل موظف موجود إلى فرع آخر
      if (employee && oldBranchId && newBranchId && oldBranchId !== newBranchId) {
        const oldBranchName = isRTL ? (employee as any).branchNameAr || (employee as any).branchName || `#${oldBranchId}` : (employee as any).branchName || `#${oldBranchId}`;
        const newBranchName = isRTL ? selectedBranch?.branchNameAr || selectedBranch?.branchName || `#${newBranchId}` : selectedBranch?.branchName || `#${newBranchId}`;
        const msg = isRTL
          ? `سيتم تحويل الموظف من فرع "${oldBranchName}" إلى فرع "${newBranchName}".\n\nسيتم نقل كل ساعات التايم شيت (الرسمي، الإضافي، الأوف، الإجازات الرسمية) للفترة المفتوحة (الشهر الحالي الذي لم تُرحَّل مرتباته بعد) إلى الفرع الجديد بالكامل.\n\nالأشهر السابقة المرحَّلة مرتباتها ستبقى في الفرع القديم بلا أي تغيير.\n\nهل تريد المتابعة؟`
          : `Employee will be transferred from "${oldBranchName}" to "${newBranchName}".\n\nAll timesheet hours (Regular, Overtime, Off, Public Holiday) for the open period (current month with no posted payroll yet) will be fully moved to the new branch.\n\nPreviously posted-payroll months will remain in the old branch unchanged.\n\nContinue?`;
        const ok = await confirm({ message: msg, severity: 'warning' });
        if (!ok) return;
      }

      setSaving(true);
      const dataToSave = {
        ...formData,
        branchId: newBranchId,
        bankId: selectedBank?.bankId,
        bankName: selectedBank ? (isRTL ? selectedBank.bankNameAr : selectedBank.bankName) : formData.bankName,
      };

      if (employee) {
        await employeesService.update(employee.employeeId, dataToSave);
        toast.success(t.updateSuccess, { autoClose: 2000 });
      } else {
        await employeesService.create(dataToSave);
        toast.success(t.saveSuccess, { autoClose: 2000 });
      }
      onSave();
      handleClose();
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error(t.saveError, { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      employeeId: 0,
      employeeCode: '',
      fullName: '',
      fullNameAr: '',
      branchId: undefined,
      departmentId: undefined,
      jobTitle: '',
      phone: '',
      email: '',
      hireDate: new Date().toISOString().split('T')[0],
      isActive: true,
      employeeType: 'Branch',
      dailyWorkHours: 8.000,
      basicSalary: 0.000,
      housingAllowance: 0.000,
      transportAllowance: 0.000,
      otherAllowances: 0.000,
      overtimeCalculationBase: 'FromBasicSalary',
      overtimeMultiplier: 1.500,
      overtimeHourlyRate: 0.000,
      publicHolidayCalculationBase: 'FromBasicSalary',
      publicHolidayMultiplier: 2.000,
      publicHolidayHourlyRate: 0.000,
      offDayCalculationBase: 'FromBasicSalary',
      offDayMultiplier: 1.500,
      offDayHourlyRate: 0.000,
      bankAccountNumber: '',
      bankName: 'Burgan Bank',
      iban: '',
      bankCardName: '',
      companyFileNumber: '000000000000',
      bankFileType: 'Regular',
      basicSalaryPaymentMethod: 'BankTransfer',
      allowancesPaymentMethod: 'BankTransfer',
      overtimePaymentMethod: 'Cash',
      holidayPaymentMethod: 'Cash',
      offDayPaymentMethod: 'Cash'
    });
    setSelectedBranch(null);
    setSelectedBank(null);
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 24,
          background: theme.palette.background.paper
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {employee ? t.titleEdit : t.titleNew}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        px: 0, 
        pt: 0,
        pb: 2,
        background: theme.palette.background.default
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ 
            px: 3,
            pt: 2,
            pb: 2,
            mb: 3,
            borderBottom: 2,
            borderColor: 'divider',
            background: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
            '& .MuiTab-root': {
              minHeight: 60,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#555',
              px: 4,
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.main,
                fontWeight: 700,
                background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
                borderRadius: '8px 8px 0 0'
              },
              '&:hover': {
                background: theme.palette.mode === 'dark' ? '#3a3a3a' : '#fafafa',
                color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.main
              }
            },
            '& .MuiTabs-indicator': {
              height: 4,
              borderRadius: '4px 4px 0 0',
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label={t.basicInfoTab} />
          <Tab icon={<MoneyIcon />} iconPosition="start" label={t.salarySettingsTab} />
        </Tabs>
        
        <Box sx={{ px: 3 }}>
        {/* Basic Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Employee Code & Type */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#1565c0' : '#e3f2fd',
                border: '2px solid #1976d2'
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  label={t.employeeCode}
                  value={formData.employeeCode}
                  onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
                  size="small"
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: theme.palette.mode === 'dark' ? '#263238' : 'white',
                      fontWeight: 600
                    }
                  }}
                />
                <TextField
                  select
                  label={t.employeeType}
                  value={formData.employeeType || 'Branch'}
                  onChange={(e) => setFormData({ ...formData, employeeType: e.target.value })}
                  size="small"
                >
                  <MenuItem value="Management">{t.management}</MenuItem>
                  <MenuItem value="Branch">{t.branchEmployee}</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Personal Information */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.primary.main }}>
                {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  label={t.fullName}
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  size="small"
                  required
                />
                <TextField
                  label={t.fullNameAr}
                  value={formData.fullNameAr}
                  onChange={(e) => setFormData({ ...formData, fullNameAr: e.target.value })}
                  size="small"
                  required
                />
                <TextField
                  label={t.jobTitle}
                  value={formData.jobTitle || ''}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  size="small"
                />
                <Autocomplete
                  options={branches}
                  getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
                  value={selectedBranch}
                  onChange={(_, newValue) => setSelectedBranch(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label={t.branch} size="small" />
                  )}
                />
              </Box>
            </Box>

            {/* Contact Information */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.primary.main }}>
                {isRTL ? 'معلومات الاتصال' : 'Contact Information'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <TextField
                  label={t.phone}
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  size="small"
                />
                <TextField
                  label={t.email}
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  size="small"
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.hireDate}
                    value={formData.hireDate ? new Date(formData.hireDate) : null}
                    onChange={(date) =>
                      setFormData({
                        ...formData,
                        hireDate: date
                          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                          : '',
                      })
                    }
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    slotProps={{
                      textField: { size: 'small', fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Salary Settings Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Work Hours & Salary Components */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#1b5e20' : '#c8e6c9',
                border: '2px solid #4caf50'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MoneyIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#1b5e20' }}>
                  {isRTL ? 'الراتب والبدلات' : 'Salary & Allowances'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <TextField
                  type="number"
                  label={t.dailyWorkHours}
                  value={formData.dailyWorkHours || 8.000}
                  onChange={(e) => setFormData({ ...formData, dailyWorkHours: parseFloat(e.target.value) || 8.000 })}
                  size="small"
                  inputProps={{ min: 0, max: 24, step: 0.001 }}
                />
                <TextField
                  type="number"
                  label={`${t.basicSalary} (${t.kwd})`}
                  value={formData.basicSalary || 0.000}
                  onChange={(e) => setFormData({ ...formData, basicSalary: parseFloat(e.target.value) || 0.000 })}
                  size="small"
                  inputProps={{ min: 0, step: 0.001 }}
                />
                <TextField
                  type="number"
                  label={`${t.housingAllowance} (${t.kwd})`}
                  value={formData.housingAllowance || 0.000}
                  onChange={(e) => setFormData({ ...formData, housingAllowance: parseFloat(e.target.value) || 0.000 })}
                  size="small"
                  inputProps={{ min: 0, step: 0.001 }}
                />
                <TextField
                  type="number"
                  label={`${t.transportAllowance} (${t.kwd})`}
                  value={formData.transportAllowance || 0.000}
                  onChange={(e) => setFormData({ ...formData, transportAllowance: parseFloat(e.target.value) || 0.000 })}
                  size="small"
                  inputProps={{ min: 0, step: 0.001 }}
                />
                <TextField
                  type="number"
                  label={`${t.otherAllowances} (${t.kwd})`}
                  value={formData.otherAllowances || 0.000}
                  onChange={(e) => setFormData({ ...formData, otherAllowances: parseFloat(e.target.value) || 0.000 })}
                  size="small"
                  inputProps={{ min: 0, step: 0.001 }}
                  sx={{ gridColumn: 'span 2' }}
                />
              </Box>
            </Paper>

            {/* Overtime Calculation */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#e65100' : '#ffe0b2',
                border: '2px solid #ff9800'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ScheduleIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#e65100' }}>
                  {t.overtimeCalculation}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1, color: 'text.secondary' }}>
                    {t.calculationBase}
                  </FormLabel>
                  <RadioGroup
                    value={formData.overtimeCalculationBase || 'FromBasicSalary'}
                    onChange={(e) => setFormData({ ...formData, overtimeCalculationBase: e.target.value })}
                  >
                    <FormControlLabel 
                      value="FromBasicSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromBasicSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FromTotalSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromTotalSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FixedHourlyRate" 
                      control={<Radio size="small" />} 
                      label={t.fixedHourlyRate}
                    />
                  </RadioGroup>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.overtimeCalculationBase === 'FixedHourlyRate' ? (
                    <TextField
                      type="number"
                      label={t.hourlyRate}
                      value={formData.overtimeHourlyRate || 0.000}
                      onChange={(e) => setFormData({ ...formData, overtimeHourlyRate: parseFloat(e.target.value) || 0.000 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 3.500 دينار للساعة' : 'Example: 3.500 KWD per hour'}
                    />
                  ) : (
                    <TextField
                      type="number"
                      label={t.multiplier}
                      value={formData.overtimeMultiplier || 1.500}
                      onChange={(e) => setFormData({ ...formData, overtimeMultiplier: parseFloat(e.target.value) || 1.500 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 1.500 = ساعة ونصف' : 'Example: 1.500 = time and half'}
                    />
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Public Holiday Calculation */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#01579b' : '#b3e5fc',
                border: '2px solid #03a9f4'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#03a9f4', fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#01579b' }}>
                  {t.publicHolidayCalculation}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1, color: 'text.secondary' }}>
                    {t.calculationBase}
                  </FormLabel>
                  <RadioGroup
                    value={formData.publicHolidayCalculationBase || 'FromBasicSalary'}
                    onChange={(e) => setFormData({ ...formData, publicHolidayCalculationBase: e.target.value })}
                  >
                    <FormControlLabel 
                      value="FromBasicSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromBasicSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FromTotalSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromTotalSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FixedHourlyRate" 
                      control={<Radio size="small" />} 
                      label={t.fixedHourlyRate}
                    />
                  </RadioGroup>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.publicHolidayCalculationBase === 'FixedHourlyRate' ? (
                    <TextField
                      type="number"
                      label={t.hourlyRate}
                      value={formData.publicHolidayHourlyRate || 0.000}
                      onChange={(e) => setFormData({ ...formData, publicHolidayHourlyRate: parseFloat(e.target.value) || 0.000 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 5.000 دينار للساعة' : 'Example: 5.000 KWD per hour'}
                    />
                  ) : (
                    <TextField
                      type="number"
                      label={t.multiplier}
                      value={formData.publicHolidayMultiplier || 2.000}
                      onChange={(e) => setFormData({ ...formData, publicHolidayMultiplier: parseFloat(e.target.value) || 2.000 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 2.000 = ضعف' : 'Example: 2.000 = double'}
                    />
                  )}
                </Box>
              </Box>
            </Paper>

            {/* OFF Day (Weekly Rest) Calculation */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#1b5e20' : '#c8e6c9',
                border: '2px solid #4caf50'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#1b5e20' }}>
                  {isRTL ? 'حساب يوم الراحة (OFF)' : 'OFF Day Calculation'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1, color: 'text.secondary' }}>
                    {t.calculationBase}
                  </FormLabel>
                  <RadioGroup
                    value={formData.offDayCalculationBase || 'FromBasicSalary'}
                    onChange={(e) => setFormData({ ...formData, offDayCalculationBase: e.target.value })}
                  >
                    <FormControlLabel 
                      value="FromBasicSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromBasicSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FromTotalSalary" 
                      control={<Radio size="small" />} 
                      label={t.fromTotalSalary}
                      sx={{ mb: 0.5 }}
                    />
                    <FormControlLabel 
                      value="FixedHourlyRate" 
                      control={<Radio size="small" />} 
                      label={t.fixedHourlyRate}
                    />
                  </RadioGroup>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.offDayCalculationBase === 'FixedHourlyRate' ? (
                    <TextField
                      type="number"
                      label={t.hourlyRate}
                      value={formData.offDayHourlyRate || 0.000}
                      onChange={(e) => setFormData({ ...formData, offDayHourlyRate: parseFloat(e.target.value) || 0.000 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 1.500 دينار للساعة' : 'Example: 1.500 KWD per hour'}
                    />
                  ) : (
                    <TextField
                      type="number"
                      label={t.multiplier}
                      value={formData.offDayMultiplier || 1.500}
                      onChange={(e) => setFormData({ ...formData, offDayMultiplier: parseFloat(e.target.value) || 1.500 })}
                      size="small"
                      inputProps={{ min: 0, step: 0.001 }}
                      helperText={isRTL ? 'مثال: 1.500 = مرة ونصف' : 'Example: 1.500 = time and half'}
                    />
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Bank Information */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#4a148c' : '#e1bee7',
                border: '2px solid #9c27b0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BankIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#4a148c' }}>
                  {t.bankInfo}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <Autocomplete
                  options={banks}
                  getOptionLabel={(option) => isRTL ? option.bankNameAr : option.bankName}
                  value={selectedBank}
                  onChange={(_, newValue) => {
                    setSelectedBank(newValue);
                    setFormData({ 
                      ...formData,
                      bankId: newValue?.bankId,
                      bankName: newValue ? (isRTL ? newValue.bankNameAr : newValue.bankName) : ''
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t.bankName} size="small" />
                  )}
                />
                <TextField
                  label={t.accountNumber}
                  value={formData.bankAccountNumber || ''}
                  onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                  size="small"
                />
                <TextField
                  label={t.iban}
                  value={formData.iban || ''}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  size="small"
                />
              </Box>

              {/* ✅ NEW FIELDS - Bank Card Name & Company File Number */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
                <TextField
                  label={t.bankCardName}
                  value={formData.bankCardName || ''}
                  onChange={(e) => setFormData({ ...formData, bankCardName: e.target.value })}
                  size="small"
                  helperText={isRTL ? 'الاسم بالضبط كما في كارت البنك (إنجليزي)' : 'Exact name on bank card (English)'}
                  placeholder="AHMED MOHAMMED SALEM"
                />
                <TextField
                  label={t.companyFileNumber}
                  value={formData.companyFileNumber || '000000000000'}
                  onChange={(e) => setFormData({ ...formData, companyFileNumber: e.target.value })}
                  size="small"
                  helperText={isRTL ? 'رقم ملف الشركة في الشؤون (افتراضي: 000000000000)' : 'Company file number (default: 000000000000)'}
                />
              </Box>
              
              <Box>
                <TextField
                  select
                  fullWidth
                  label={t.bankFileType}
                  value={formData.bankFileType || 'Regular'}
                  onChange={(e) => setFormData({ ...formData, bankFileType: e.target.value })}
                  size="small"
                  helperText={formData.bankFileType === 'Worker' ? (isRTL ? 'سيظهر في ملف بي دينار (حساب العمال)' : 'Will appear in Worker file (BD account)') : (isRTL ? 'سيظهر في ملف الرواتب العادي' : 'Will appear in regular salary file')}
                >
                  <MenuItem value="Regular">{t.regularSalary}</MenuItem>
                  <MenuItem value="Worker">{t.workerSalary}</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Payment Methods */}
            <Paper 
              elevation={3}
              sx={{ 
                p: 2.5, 
                borderRadius: 2,
                background: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5',
                border: '2px solid #757575'
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: theme.palette.mode === 'dark' ? '#fff' : '#424242' }}>
                {t.paymentMethod}
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  select
                  label={isRTL ? 'الراتب الأساسي' : 'Basic Salary'}
                  value={formData.basicSalaryPaymentMethod || 'BankTransfer'}
                  onChange={(e) => setFormData({ ...formData, basicSalaryPaymentMethod: e.target.value })}
                  size="small"
                >
                  <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
                  <MenuItem value="Cash">{t.cash}</MenuItem>
                </TextField>

                <TextField
                  select
                  label={isRTL ? 'البدلات' : 'Allowances'}
                  value={formData.allowancesPaymentMethod || 'BankTransfer'}
                  onChange={(e) => setFormData({ ...formData, allowancesPaymentMethod: e.target.value })}
                  size="small"
                >
                  <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
                  <MenuItem value="Cash">{t.cash}</MenuItem>
                </TextField>

                <TextField
                  select
                  label={isRTL ? 'الوقت الإضافي' : 'Overtime'}
                  value={formData.overtimePaymentMethod || 'Cash'}
                  onChange={(e) => setFormData({ ...formData, overtimePaymentMethod: e.target.value })}
                  size="small"
                >
                  <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
                  <MenuItem value="Cash">{t.cash}</MenuItem>
                </TextField>

                <TextField
                  select
                  label={isRTL ? 'العطلات الرسمية' : 'Public Holidays'}
                  value={formData.holidayPaymentMethod || 'Cash'}
                  onChange={(e) => setFormData({ ...formData, holidayPaymentMethod: e.target.value })}
                  size="small"
                >
                  <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
                  <MenuItem value="Cash">{t.cash}</MenuItem>
                </TextField>

                <TextField
                  select
                  label={isRTL ? 'يوم الراحة (OFF)' : 'OFF Day'}
                  value={formData.offDayPaymentMethod || 'Cash'}
                  onChange={(e) => setFormData({ ...formData, offDayPaymentMethod: e.target.value })}
                  size="small"
                >
                  <MenuItem value="BankTransfer">{t.bankTransfer}</MenuItem>
                  <MenuItem value="Cash">{t.cash}</MenuItem>
                </TextField>
              </Box>
            </Paper>
          </Box>
        </TabPanel>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        gap: 1,
        background: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`
      }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          sx={{ minWidth: 100 }}
        >
          {t.cancel}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={saving}
          sx={{ 
            minWidth: 100,
            fontWeight: 600,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6
            }
          }}
        >
          {saving ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeDialog;